import type { Exercise } from '../../types.ts';
export const batch5Foundations: Exercise[] = [
  {
    id: 'foundations-heat',
    category: 'foundations',
    title: 'Heat and temperature',
    topic: 'Heat and temperature',
    difficulty: 'Challenging',
    reading: {
      title: 'Heat and temperature',
      sections: [
        {
          id: 'temperature',
          title: 'Temperature is not an inventory of energy',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Touching a cool spoon and a warm mug gives information about energy transfer, but touch is not a direct measurement of an object’s total internal energy. Temperature describes thermal state and determines the direction of spontaneous heat transfer between objects in thermal contact. Internal energy concerns energy stored in microscopic motions and interactions. Mass, material and state matter as well as temperature.',
            },
            {
              kind: 'paragraph',
              text: 'Heat is energy transferred because of a temperature difference. It is not a substance stored in an object. When two bodies interact without an external driving process, the net transfer is from higher to lower temperature. At thermal equilibrium their temperatures agree. Microscopic exchanges continue, but they balance, so there is no net heat flow between the bodies.',
            },
            {
              kind: 'paragraph',
              text: 'For an ideal gas, average translational kinetic energy is proportional to absolute temperature. This useful microscopic model should not be stretched into a claim that every material’s internal energy is determined solely by one thermometer reading. A large bath can require much more energy to warm than a tiny cup even when both begin at the same temperature.',
            },
            {
              kind: 'example',
              title: 'Split a warm sample',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Divide a well-mixed cup of water into two equal cups without cooling it. Each part has the original temperature, but roughly half the original mass and thermal energy inventory relative to the same reference state. Temperature does not halve. This distinction is why size matters in heating calculations even when the thermometer readings match.',
                },
              ],
            },
          ],
        },
        {
          id: 'capacity',
          title: 'How much energy changes a temperature?',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Specific heat capacity c is the energy needed per unit mass for a unit temperature increase, under stated conditions. For a fixed material and phase over a modest range, treating c as constant gives a useful approximation. An object of mass m has heat capacity mc: two kilograms require twice the energy of one kilogram for the same change, if all other conditions match.',
            },
            {
              kind: 'equation',
              label: 'Heating without a phase change',
              lines: [
                {
                  left: 'Q',
                  right: 'mc(Tfinal − Tinitial)',
                  reason:
                    'Q is energy transferred in joules; m is kilograms and c is joules per kilogram per degree.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'The equation follows from the meaning of energy per kilogram per degree: multiply by the kilograms and the number of degrees. It is not a claim that c is identical for all materials. A large c means more energy is needed for a given temperature change, not necessarily that energy moves through the material slowly. Thermal conductivity and heat capacity answer different questions.',
            },
            {
              kind: 'example',
              title: 'Heating a block',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'A 0.4 kg block has c=500 J/(kg·°C). Raising its temperature by 15°C requires 0.4×500×15=3,000 J. If the same energy enters a 0.8 kg block of that material, the increase is 3,000/(0.8×500)=7.5°C.',
                },
                {
                  kind: 'table',
                  caption: 'Hold the input energy fixed',
                  headers: ['Mass', 'Heat capacity', 'Temperature rise'],
                  rows: [
                    ['0.4 kg', '200 J/°C', '15°C'],
                    ['0.8 kg', '400 J/°C', '7.5°C'],
                  ],
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'A temperature change of one Celsius degree is the same size as a change of one kelvin. Absolute temperatures are different: 20°C is not 20 K. In this equation a difference is required; using the final temperature alone implicitly assumes an initial temperature of zero on that scale.',
            },
          ],
        },
        {
          id: 'balance',
          title: 'Mixing is an energy balance',
          blocks: [
            {
              kind: 'paragraph',
              text: 'In an insulated mixing calculation, energy lost by the warmer component equals energy gained by the cooler component, provided the container’s energy change and other transfers are negligible. Both components finish at one equilibrium temperature. The result is a weighted average when neither changes phase and their heat capacities can be treated as constant.',
            },
            {
              kind: 'example',
              title: 'Unequal amounts of the same liquid',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Mix 1 kg at 80°C with 3 kg at 20°C, assuming identical specific heats, no loss and no phase change. Write 1c(80−T)=3c(T−20). Cancel c, giving 80−T=3T−60, so T=35°C. The larger cold mass pulls the result closer to 20 than to 80.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'A simple arithmetic average would give 50°C and silently give each kilogram on the hot side more influence than each kilogram on the cold side. Weight by heat capacity, not by the number of containers. A result outside the two starting temperatures would signal an omitted energy source, a phase-change complication, or a calculation error.',
            },
            {
              kind: 'paragraph',
              text: 'During melting or boiling at fixed pressure, added energy can change the phase without raising temperature. Q=mcΔT alone then misses energy associated with the transition. A flat segment on a heating curve does not mean the heater stopped transferring energy; it means temperature is not tracking all the internal changes.',
            },
          ],
        },
        {
          id: 'transfer',
          title: 'Three routes for energy transfer',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Conduction transfers energy through microscopic interactions without requiring bulk motion of the material. Convection involves energy carried by moving fluid, meaning liquid or gas. Thermal radiation travels as electromagnetic waves and can cross a vacuum. Several mechanisms can operate in the same scene, so identify the specific part of the process being described.',
            },
            {
              kind: 'table',
              caption: 'Follow the stated path',
              headers: ['Observation', 'Dominant named mechanism'],
              rows: [
                ['Energy travels along a metal handle', 'Conduction'],
                ['A fan carries warm air across a room', 'Forced convection'],
                ['Sunlight warms a surface across space', 'Radiation'],
              ],
            },
            {
              kind: 'paragraph',
              text: 'A metal surface can feel colder than wood at the same room temperature because it draws energy from warmer skin more rapidly. Feeling colder does not establish a lower initial temperature. Conversely, a well-insulated container slows exchange but does not supply a permanent source of cooling. Given enough time in a warmer room, its contents approach room temperature.',
            },
            {
              kind: 'paragraph',
              text: 'When applying these ideas to the quiz, first decide whether the question asks about temperature, energy amount or transfer rate. Then identify mass and material, the system boundary and whether a phase changes. That sequence distinguishes the doubled-mass comparison from the mixing calculation and the cold-feeling handle from a thermometer measurement.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'OpenStax Physics · Heat, specific heat and heat transfer',
          url: 'https://openstax.org/books/physics/pages/11-2-heat-specific-heat-and-heat-transfer',
        },
      ],
      authorship:
        'Original teaching, examples and questions prepared with AI assistance; references support factual checks.',
    },
    questions: [
      {
        id: 'b5-heat-1',
        prompt:
          'Two different-sized samples of the same liquid are at the same temperature. Which conclusion follows?',
        options: [
          {
            id: '0',
            text: 'They contain equal total internal energy.',
          },
          {
            id: '1',
            text: 'They have equal heat capacities.',
          },
          {
            id: '2',
            text: 'No net heat flows between them solely because of temperature when placed in thermal contact.',
          },
          {
            id: '3',
            text: 'Their masses must be equal.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Equal temperatures imply thermal equilibrium, not equal energy inventories or heat capacities.',
        evidence:
          'Equal temperatures imply thermal equilibrium, not equal energy inventories or heat capacities.',
        sectionIds: ['temperature'],
      },
      {
        id: 'b5-heat-2',
        prompt:
          'With the same material and phase, double the mass while adding the same energy. How does the temperature increase change?',
        options: [
          {
            id: '0',
            text: 'It halves.',
          },
          {
            id: '1',
            text: 'It doubles.',
          },
          {
            id: '2',
            text: 'It remains unchanged.',
          },
          {
            id: '3',
            text: 'It quadruples.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation: 'ΔT=Q/(mc); doubling m with Q and c fixed halves the increase.',
        evidence: 'ΔT=Q/(mc); doubling m with Q and c fixed halves the increase.',
        sectionIds: ['temperature', 'capacity'],
      },
      {
        id: 'b5-heat-3',
        prompt:
          'A 2 kg object has c=300 J/(kg·°C). How much energy raises its temperature by 5°C without phase change?',
        options: [
          {
            id: '0',
            text: '120 J',
          },
          {
            id: '1',
            text: '600 J',
          },
          {
            id: '2',
            text: '1,500 J',
          },
          {
            id: '3',
            text: '3,000 J',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Multiply mass, energy per kilogram per degree, and temperature rise: Q=2×300×5=3000 J.',
        evidence:
          'Multiply mass, energy per kilogram per degree, and temperature rise: Q=2×300×5=3000 J.',
        sectionIds: ['capacity'],
      },
      {
        id: 'b5-heat-4',
        prompt: 'Why does metal often feel colder than wood in the same cool room?',
        options: [
          {
            id: '0',
            text: 'Metal must have a lower equilibrium temperature.',
          },
          {
            id: '1',
            text: 'Metal can remove energy from skin faster.',
          },
          {
            id: '2',
            text: 'Wood has no internal energy.',
          },
          {
            id: '3',
            text: 'Metal transfers cold particles into skin.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Different transfer rates produce different sensations even with equal initial temperatures.',
        evidence:
          'Different transfer rates produce different sensations even with equal initial temperatures.',
        sectionIds: ['temperature', 'transfer'],
      },
      {
        id: 'b5-heat-5',
        prompt:
          'Mix 2 kg of water at 70°C with 1 kg at 10°C, insulated with negligible container capacity and no phase change. Final temperature?',
        options: [
          {
            id: '0',
            text: '40°C',
          },
          {
            id: '1',
            text: '50°C',
          },
          {
            id: '2',
            text: '60°C',
          },
          {
            id: '3',
            text: '80°C',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Conservation of energy gives 2(70−T)=T−10, so the common temperature is 50°C.',
        evidence: 'Conservation of energy gives 2(70−T)=T−10, so the common temperature is 50°C.',
        sectionIds: ['capacity', 'balance'],
      },
      {
        id: 'b5-heat-6',
        prompt:
          'A pure substance melts at constant pressure while its temperature stays constant. What can still be happening?',
        options: [
          {
            id: '0',
            text: 'Its internal energy must be falling.',
          },
          {
            id: '1',
            text: 'Its mass must be doubling.',
          },
          {
            id: '2',
            text: 'No energy can be entering.',
          },
          {
            id: '3',
            text: 'Energy is entering to change its phase.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation: 'A phase transition can absorb energy without raising temperature.',
        evidence: 'A phase transition can absorb energy without raising temperature.',
        sectionIds: ['temperature', 'balance'],
      },
      {
        id: 'b5-heat-7',
        prompt: 'Which transfer mechanism can carry energy across empty space?',
        options: [
          {
            id: '0',
            text: 'Radiation',
          },
          {
            id: '1',
            text: 'Convection',
          },
          {
            id: '2',
            text: 'Conduction',
          },
          {
            id: '3',
            text: 'Bulk fluid circulation',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation: 'Electromagnetic radiation needs no material medium.',
        evidence: 'Electromagnetic radiation needs no material medium.',
        sectionIds: ['transfer'],
      },
      {
        id: 'b5-heat-8',
        prompt:
          'A fan moves heated air across a room. Which mechanism describes energy carried with that air?',
        options: [
          {
            id: '0',
            text: 'Conduction only',
          },
          {
            id: '1',
            text: 'Radiation only',
          },
          {
            id: '2',
            text: 'Forced convection',
          },
          {
            id: '3',
            text: 'A phase transition',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'Bulk movement of a fluid carries energy by convection.',
        evidence: 'Bulk movement of a fluid carries energy by convection.',
        sectionIds: ['transfer'],
      },
      {
        id: 'b5-heat-9',
        prompt: 'Which unit matches specific heat capacity when mass is measured in kilograms?',
        options: [
          {
            id: '0',
            text: 'J/°C',
          },
          {
            id: '1',
            text: 'J/(kg·°C)',
          },
          {
            id: '2',
            text: 'kg·J/°C',
          },
          {
            id: '3',
            text: '°C/J',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Specific heat is energy per mass per degree, unlike an object’s heat capacity.',
        evidence: 'Specific heat is energy per mass per degree, unlike an object’s heat capacity.',
        sectionIds: ['capacity'],
      },
      {
        id: 'b5-heat-10',
        prompt:
          'A warmer object is perfectly insulated from a cooler object. What does the temperature difference alone guarantee?',
        options: [
          {
            id: '0',
            text: 'Nothing about heat transfer between them if no transfer path exists.',
          },
          {
            id: '1',
            text: 'Immediate equality of their temperatures.',
          },
          {
            id: '2',
            text: 'Equal total internal energies.',
          },
          {
            id: '3',
            text: 'A constant nonzero transfer rate.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Temperature determines the net direction when a transfer path exists; ideal isolation prevents exchange.',
        evidence:
          'Temperature determines the net direction when a transfer path exists; ideal isolation prevents exchange.',
        sectionIds: ['temperature', 'transfer'],
      },
    ],
  },
  {
    id: 'foundations-selection',
    category: 'foundations',
    title: 'Natural selection and population change',
    topic: 'Natural selection and population change',
    difficulty: 'Challenging',
    reading: {
      title: 'Natural selection and population change',
      sections: [
        {
          id: 'variation',
          title: 'Begin with differences already present',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A population is a group of organisms of the same species considered together, often because they reproduce in a shared area. Individuals vary: they may differ in color, timing, body size or behavior. Some differences are heritable, meaning genetic differences contribute to offspring resembling their parents. Others arise mainly from experience or immediate environmental conditions.',
            },
            {
              kind: 'paragraph',
              text: 'Natural selection occurs when heritable differences influence reproductive success in a particular environment. Organisms with some variants contribute more descendants, so those variants can become more common across generations. The population changes through unequal contributions; an individual does not need to transform itself deliberately into the favored type.',
            },
            {
              kind: 'paragraph',
              text: 'Three links must be checked: variation exists, some of that variation can be inherited, and it is associated with different reproductive outcomes. A temporary coat of dust might make an animal less visible, but dust acquired on an individual is not automatically inherited. Conversely, a heritable color difference has no selective effect in an environment where it does not affect reproduction or survival leading to reproduction.',
            },
            {
              kind: 'example',
              title: 'A stipulated beetle population',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Suppose a population begins with 50 light and 50 dark beetles. Color is inherited reliably. On dark bark, each dark beetle leaves an average of four surviving offspring and each light beetle two. Expected offspring counts are 200 dark and 100 light. Dark beetles rise from half to two thirds of the next generation, without any light beetle changing color.',
                },
              ],
            },
          ],
        },
        {
          id: 'fitness',
          title: 'Fitness is relative reproductive contribution',
          blocks: [
            {
              kind: 'paragraph',
              text: 'In evolutionary explanations, fitness is measured through reproductive contribution under specified conditions, not athletic strength or moral worth. Living longer can help, but an organism that survives for years without leaving descendants does not thereby contribute more genes than a shorter-lived organism that reproduces successfully. Comparisons must use the same relevant population and interval.',
            },
            {
              kind: 'table',
              caption: 'A simplified generation',
              headers: ['Type', 'Starting adults', 'Offspring per adult', 'Total offspring'],
              rows: [
                ['X', '20', '3', '60'],
                ['Y', '40', '1', '40'],
              ],
            },
            {
              kind: 'paragraph',
              text: 'Type X begins as one third of adults but contributes three fifths of offspring. Absolute counts and relative frequencies tell different parts of the story. Both types can increase numerically while the share of one declines. Selection concerns differential representation, so merely noting that a population grew is not enough to identify which trait was favored.',
            },
            {
              kind: 'paragraph',
              text: 'An advantage depends on the environment. A thicker coat can reduce heat loss in a cold setting while increasing overheating costs in a hot one. Traits can also involve trade-offs: allocating energy to one function can reduce resources for another. Selection does not promise a universally best organism, nor can it choose a variant absent from the population.',
            },
            {
              kind: 'paragraph',
              text: 'Changing the environment can change which existing variants leave more descendants. It does not mean the environment supplies exactly the mutation that would be useful. Mutations generate new genetic variants; recombination rearranges existing ones. Selection filters consequences after variation exists.',
            },
          ],
        },
        {
          id: 'patterns',
          title: 'Different patterns need different explanations',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Directional selection favors one end of a trait distribution and can shift the average. Stabilizing selection favors intermediate values and can reduce extremes. Disruptive selection favors both extremes over intermediate forms. These labels describe how reproductive success relates to the trait; they are not three intentions pursued by the organisms.',
            },
            {
              kind: 'example',
              title: 'Seeds of different sizes',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Suppose only very small and very large seeds are abundant. Small-beaked birds efficiently handle the small seeds, large-beaked birds the large ones, and intermediate beaks handle neither well. If these differences are inherited and affect reproduction, the setup illustrates disruptive selection. If medium seeds were most abundant and medium beaks did best, the pattern would instead be stabilizing.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Frequency-dependent selection occurs when a variant’s advantage changes with how common it is. For example, a predator learning to recognize the common prey appearance can give a rare appearance a temporary advantage. As rarity changes, so can the advantage. Such dynamics can help maintain variation rather than driving one type permanently to fixation.',
            },
            {
              kind: 'paragraph',
              text: 'These patterns do not establish that every observed difference is adaptive. A trait can be carried along with other genetic changes or reflect chance. A convincing selective explanation connects a specific trait, mechanism and reproductive consequence rather than inventing a benefit after noticing that the trait exists.',
            },
          ],
        },
        {
          id: 'other',
          title: 'Evolution is broader than natural selection',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Genetic drift is change in variant frequencies due to chance sampling across generations. Its relative effects are often stronger in small populations. If a storm kills organisms independently of their inherited traits, the surviving frequencies can change without those traits providing an advantage. A bottleneck sharply reduces population size and can remove variation by chance.',
            },
            {
              kind: 'paragraph',
              text: 'Gene flow moves variants between populations through migration and reproduction. A new arrival matters genetically when it or its descendants contribute to the receiving population. Mutation introduces new variants. These processes can operate alongside selection, and distinguishing them requires information about why frequencies changed.',
            },
            {
              kind: 'table',
              caption: 'Identify the mechanism',
              headers: ['Stated cause of frequency change', 'Mechanism'],
              rows: [
                ['Heritable trait improves reproductive contribution', 'Natural selection'],
                ['Random survivors happen to carry different variants', 'Genetic drift'],
                ['Migrants reproduce in the population', 'Gene flow'],
                ['A DNA change creates a new variant', 'Mutation'],
              ],
            },
            {
              kind: 'paragraph',
              text: 'For the quiz, avoid reading purpose into a population trend. If a color becomes common after background conditions change, ask whether color affected survival and whether it was inherited. If a few founders happen to carry that color, chance sampling may be sufficient. The same visible outcome can arise from different mechanisms; the causal details in the prompt determine the answer.',
            },
            {
              kind: 'paragraph',
              text: 'A final distinction concerns timescale. An individual becoming stronger through exercise is a change during its lifetime. A heritable variant becoming more frequent over generations is population evolution. Both are biological changes, but substituting one for the other obscures the inheritance and reproduction that evolutionary explanations require.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'OpenStax Biology 2e · Adaptive evolution',
          url: 'https://openstax.org/books/biology-2e/pages/19-3-adaptive-evolution',
        },
        {
          title: 'OpenStax Biology 2e · Population genetics',
          url: 'https://openstax.org/books/biology-2e/pages/19-2-population-genetics',
        },
      ],
      authorship:
        'Original teaching, examples and questions prepared with AI assistance; references support factual checks.',
    },
    questions: [
      {
        id: 'b5-selection-1',
        prompt:
          'Which combination is sufficient for natural selection to change a trait’s frequency across generations?',
        options: [
          {
            id: '0',
            text: 'Variation alone, regardless of inheritance',
          },
          {
            id: '1',
            text: 'A shared need experienced equally by all individuals',
          },
          {
            id: '2',
            text: 'Heritable variation associated with unequal reproductive success',
          },
          {
            id: '3',
            text: 'Longer lives with no change in reproductive contribution',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Selection requires heritable differences and differential reproductive contribution.',
        evidence:
          'Selection requires heritable differences and differential reproductive contribution.',
        sectionIds: ['variation', 'fitness'],
      },
      {
        id: 'b5-selection-2',
        prompt:
          'A heritable dark color improves concealment after bark becomes darker. Which account best explains a later rise in dark beetles?',
        options: [
          {
            id: '0',
            text: 'Light beetles deliberately darken their inherited genes.',
          },
          {
            id: '1',
            text: 'Dark beetles leave more descendants on average.',
          },
          {
            id: '2',
            text: 'Every mutation becomes dark because the bark requires it.',
          },
          {
            id: '3',
            text: 'The population changes without any reproduction.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Existing inherited variants can change frequency through unequal reproduction.',
        evidence: 'Existing inherited variants can change frequency through unequal reproduction.',
        sectionIds: ['variation'],
      },
      {
        id: 'b5-selection-3',
        prompt:
          'Type A has 10 adults leaving 4 offspring each; B has 30 adults leaving 1 each. What fraction of the 70 offspring comes from A?',
        options: [
          {
            id: '0',
            text: '1/4',
          },
          {
            id: '1',
            text: '1/3',
          },
          {
            id: '2',
            text: '3/7',
          },
          {
            id: '3',
            text: '4/7',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation: 'A contributes 40 of 70 offspring, or 4/7.',
        evidence: 'A contributes 40 of 70 offspring, or 4/7.',
        sectionIds: ['fitness'],
      },
      {
        id: 'b5-selection-4',
        prompt: 'Which outcome best fits genetic drift?',
        options: [
          {
            id: '0',
            text: 'A chance storm leaves a small sample with a different allele frequency, unrelated to the allele’s effect.',
          },
          {
            id: '1',
            text: 'A trait consistently improves mating success.',
          },
          {
            id: '2',
            text: 'Migrants introduce an allele through reproduction.',
          },
          {
            id: '3',
            text: 'A DNA copying change creates a new allele.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation: 'Trait-independent chance sampling is drift.',
        evidence: 'Trait-independent chance sampling is drift.',
        sectionIds: ['other'],
      },
      {
        id: 'b5-selection-5',
        prompt:
          'Intermediate values have higher reproductive success than either extreme. Which pattern is described?',
        options: [
          {
            id: '0',
            text: 'Directional',
          },
          {
            id: '1',
            text: 'Disruptive',
          },
          {
            id: '2',
            text: 'Stabilizing',
          },
          {
            id: '3',
            text: 'Gene flow',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'Stabilizing selection favors intermediates over extremes.',
        evidence: 'Stabilizing selection favors intermediates over extremes.',
        sectionIds: ['patterns'],
      },
      {
        id: 'b5-selection-6',
        prompt: 'Which statement about evolutionary fitness is most accurate?',
        options: [
          {
            id: '0',
            text: 'It always equals maximum physical strength.',
          },
          {
            id: '1',
            text: 'It concerns reproductive contribution in the relevant environment.',
          },
          {
            id: '2',
            text: 'It guarantees the longest possible lifespan.',
          },
          {
            id: '3',
            text: 'It is independent of environmental conditions.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Fitness is relative reproductive contribution, not a general strength ranking.',
        evidence: 'Fitness is relative reproductive contribution, not a general strength ranking.',
        sectionIds: ['fitness'],
      },
      {
        id: 'b5-selection-7',
        prompt:
          'A thick coat helps in cold weather but imposes overheating costs in heat. What does this illustrate?',
        options: [
          {
            id: '0',
            text: 'All useful traits must eventually disappear.',
          },
          {
            id: '1',
            text: 'Selection cannot affect temperature-related traits.',
          },
          {
            id: '2',
            text: 'Acquired traits are always inherited.',
          },
          {
            id: '3',
            text: 'A trait’s advantage can depend on environment and trade-offs.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation: 'Benefits and costs depend on context.',
        evidence: 'Benefits and costs depend on context.',
        sectionIds: ['fitness'],
      },
      {
        id: 'b5-selection-8',
        prompt:
          'A few founders establish an island population with an unusual allele frequency by chance. What is the most direct explanation?',
        options: [
          {
            id: '0',
            text: 'Genetic drift through founder sampling',
          },
          {
            id: '1',
            text: 'Selection for traits not yet present',
          },
          {
            id: '2',
            text: 'Every founder mutating identically',
          },
          {
            id: '3',
            text: 'Recombination guaranteeing equal frequencies',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation: 'A small founding sample can differ by chance.',
        evidence: 'A small founding sample can differ by chance.',
        sectionIds: ['other'],
      },
      {
        id: 'b5-selection-9',
        prompt: 'Which observation alone is NOT evidence of population evolution?',
        options: [
          {
            id: '0',
            text: 'An inherited allele increases over generations.',
          },
          {
            id: '1',
            text: 'Migrants reproduce and change allele frequencies.',
          },
          {
            id: '2',
            text: 'An individual builds muscle through exercise.',
          },
          {
            id: '3',
            text: 'A random bottleneck changes variant frequencies.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'Individual acquired change is distinct from heritable population change.',
        evidence: 'Individual acquired change is distinct from heritable population change.',
        sectionIds: ['variation', 'other'],
      },
      {
        id: 'b5-selection-10',
        prompt:
          'A rare prey appearance gains an advantage because predators focus on the common appearance. What kind of effect is this?',
        options: [
          {
            id: '0',
            text: 'Fitness determined only by age',
          },
          {
            id: '1',
            text: 'An advantage that cannot change',
          },
          {
            id: '2',
            text: 'Mutation directed by predators',
          },
          {
            id: '3',
            text: 'Frequency-dependent selection',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation: 'The variant’s advantage depends on how common it is.',
        evidence: 'The variant’s advantage depends on how common it is.',
        sectionIds: ['fitness', 'patterns'],
      },
    ],
  },
  {
    id: 'foundations-trade',
    category: 'foundations',
    title: 'Opportunity cost and trade',
    topic: 'Opportunity cost and trade',
    difficulty: 'Challenging',
    reading: {
      title: 'Opportunity cost and trade',
      sections: [
        {
          id: 'cost',
          title: 'The alternative is part of the cost',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Opportunity cost is the value of the best alternative forgone when a choice uses scarce resources. The resource might be money, land or time. Paying no money does not make a choice costless if it occupies time that could have produced something else. Nor should all imaginable alternatives be added together: the comparison is with the best feasible alternative, not many mutually incompatible plans.',
            },
            {
              kind: 'paragraph',
              text: 'Production examples make the trade-off measurable. If one hour can make either six bowls or three plates, using that hour for three plates gives up six bowls. Each plate therefore costs two bowls in forgone output. Reversing the comparison, a bowl costs half a plate. These are ratios with units, not currency prices.',
            },
            {
              kind: 'equation',
              label: 'Opportunity cost of one plate',
              lines: [
                {
                  left: 'bowls forgone / plates gained',
                  right: '6/3 = 2 bowls per plate',
                  reason: 'The same hour of resources is compared in both uses.',
                },
              ],
            },
            {
              kind: 'example',
              title: 'A time budget',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'A volunteer can translate four pages or label twelve images in an hour. Translating one page uses time that could label three images. Labeling one image costs one third of a translated page. Doubling the volunteer’s available hours doubles both possible outputs but leaves these per-unit opportunity costs unchanged.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'This linear model assumes constant productivity and that resources can be reallocated in divisible amounts. Real production can have setup costs, learning or diminishing returns. State the model before treating one fixed opportunity cost as valid for every quantity.',
            },
          ],
        },
        {
          id: 'advantage',
          title: 'Productivity and relative sacrifice are different',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Absolute advantage means producing more output with the same resources, or using fewer resources for the same output. Comparative advantage means having a lower opportunity cost. Someone can be faster at both tasks yet still benefit from exchanging outputs because their relative productivity differs across tasks.',
            },
            {
              kind: 'table',
              caption: 'Output per hour in a hypothetical workshop',
              headers: ['Worker', 'Bowls only', 'Plates only'],
              rows: [
                ['Ari', '8', '4'],
                ['Bo', '3', '3'],
              ],
            },
            {
              kind: 'paragraph',
              text: 'Ari has an absolute advantage in both products. But Ari gives up two bowls per plate, while Bo gives up only one. Bo has the comparative advantage in plates. Ari’s bowl costs half a plate, while Bo’s bowl costs one plate, so Ari has the comparative advantage in bowls.',
            },
            {
              kind: 'paragraph',
              text: 'The calculation compares each worker with their own alternative first, then compares those ratios across workers. Comparing Ari’s eight bowls directly with Bo’s three plates mixes different goods and cannot establish opportunity cost. Likewise, assigning every task to the faster worker ignores the value of that worker’s time in the task where the relative advantage is larger.',
            },
            {
              kind: 'example',
              title: 'Reallocate one hour each',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'If Ari spends an hour on plates and Bo an hour on bowls, together they make four plates and three bowls. Switching Ari to bowls and Bo to plates yields eight bowls and three plates. This particular switch sacrifices one plate for five extra bowls. Whether that exact bundle is preferred depends on needs; the productivity table alone does not supply preferences.',
                },
              ],
            },
          ],
        },
        {
          id: 'exchange',
          title: 'Find terms both sides prefer',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Trade can let each participant obtain a good at a cost below making it themselves. In the workshop model, Ari requires two bowls of forgone production to make a plate, while Bo requires one. A trade price strictly between one and two bowls per plate can benefit both at the margin, assuming they want the goods and there are no transaction costs.',
            },
            {
              kind: 'paragraph',
              text: 'For both to save resources on the traded item, the exchange rate must be more than one bowl per plate and less than two. At either endpoint, one producer gives up exactly what self-production would cost, so that producer is indifferent rather than strictly better off.',
            },
            {
              kind: 'example',
              title: 'Trade at 1.5 bowls per plate',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Ari can obtain a plate by giving up 1.5 bowls instead of sacrificing two bowls of production. Bo can exchange one plate, which costs one bowl to produce, for 1.5 bowls. Both save resources relative to producing the imported item themselves. The gain comes from different opportunity costs, not from one party making an arithmetic mistake.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Production and consumption possibilities must be distinguished. Trade does not magically increase the output a worker can personally make in an hour. It changes what they can consume by exchanging with another producer. A consumption bundle beyond one worker’s own production frontier can be feasible even though the worker’s technology has not changed.',
            },
            {
              kind: 'paragraph',
              text: 'If both workers have identical opportunity costs, this simple constant-cost model provides no specialization gain. Other reasons for trade may exist, such as variety or economies of scale, but they are not established by this two-good calculation.',
            },
          ],
        },
        {
          id: 'limits',
          title: 'A model of gains is not a distributional guarantee',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A production-possibility frontier marks combinations that fully use the modeled resources and technology. A point inside it can indicate idle resources or inefficient use; a point outside is unattainable through current domestic production alone. Moving along the frontier changes the mix. An outward shift requires something that expands possibilities, such as more resources or improved technology.',
            },
            {
              kind: 'paragraph',
              text: 'The model identifies potential gains, not a promise that every person gains from every real trade policy. Workers can face adjustment costs, and gains can be distributed unequally. Transportation, bargaining power and environmental costs can also affect the practical outcome. These considerations do not erase the opportunity-cost calculation; they limit what conclusions it can support.',
            },
            {
              kind: 'paragraph',
              text: 'In the quiz, first check whether numbers are outputs per hour or hours per unit. Higher output indicates greater productivity; lower hours per unit does. Next compute the forgone alternative in the same resource budget. Finally compare the proposed exchange rate with both opportunity costs, preserving units throughout.',
            },
            {
              kind: 'paragraph',
              text: 'For example, if one producer’s cost is two apples per pear and another’s is four, a price of three apples per pear lies between their costs. The first can sell pears for more apples than self-producing the apples would yield, while the second can buy pears more cheaply than producing them. A price of five lies outside that interval and does not supply the same mutual advantage. The direction of the exchange matters as much as the number.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'OpenStax Economics 3e · Absolute and comparative advantage',
          url: 'https://openstax.org/books/principles-economics-3e/pages/33-1-absolute-and-comparative-advantage',
        },
        {
          title: 'OpenStax Economics 3e · Production possibilities',
          url: 'https://openstax.org/books/principles-economics-3e/pages/2-2-the-production-possibilities-frontier-and-social-choices',
        },
      ],
      authorship:
        'Original teaching, examples and questions prepared with AI assistance; references support factual checks.',
    },
    questions: [
      {
        id: 'b5-trade-1',
        prompt:
          'One hour can produce 10 cups or 5 bowls. What is the opportunity cost of one bowl?',
        options: [
          {
            id: '0',
            text: 'Half a cup',
          },
          {
            id: '1',
            text: 'Two cups',
          },
          {
            id: '2',
            text: 'Five cups',
          },
          {
            id: '3',
            text: 'Ten cups',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation: 'Five bowls displace ten cups, or two cups per bowl.',
        evidence: 'Five bowls displace ten cups, or two cups per bowl.',
        sectionIds: ['cost'],
      },
      {
        id: 'b5-trade-2',
        prompt:
          'A makes 12 reports or 6 charts per day. B makes 4 reports or 4 charts. Who has comparative advantage in charts?',
        options: [
          {
            id: '0',
            text: 'A, because A makes more charts',
          },
          {
            id: '1',
            text: 'Neither, because both can make charts',
          },
          {
            id: '2',
            text: 'Both, because charts have no cost',
          },
          {
            id: '3',
            text: 'B, whose chart costs one report rather than two',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation: 'Compare each producer’s report cost per chart: A=2, B=1.',
        evidence: 'Compare each producer’s report cost per chart: A=2, B=1.',
        sectionIds: ['cost', 'advantage'],
      },
      {
        id: 'b5-trade-3',
        prompt: 'Using that same output table, who has absolute advantage in both goods?',
        options: [
          {
            id: '0',
            text: 'A',
          },
          {
            id: '1',
            text: 'B',
          },
          {
            id: '2',
            text: 'Neither',
          },
          {
            id: '3',
            text: 'Comparative advantage makes this impossible',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation: 'A produces more of each good with the same day of resources.',
        evidence: 'A produces more of each good with the same day of resources.',
        sectionIds: ['advantage'],
      },
      {
        id: 'b5-trade-4',
        prompt:
          'One producer’s plate costs 1 bowl; another’s costs 3 bowls. Which price can give both a strict gain in the simple model?',
        options: [
          {
            id: '0',
            text: '0.5 bowl per plate',
          },
          {
            id: '1',
            text: '3 bowls per plate',
          },
          {
            id: '2',
            text: '2 bowls per plate',
          },
          {
            id: '3',
            text: '4 bowls per plate',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'A price strictly between 1 and 3 allows both to beat self-production.',
        evidence: 'A price strictly between 1 and 3 allows both to beat self-production.',
        sectionIds: ['cost', 'exchange'],
      },
      {
        id: 'b5-trade-5',
        prompt:
          'A worker doubles available hours with unchanged constant productivity. What happens to per-unit opportunity cost?',
        options: [
          {
            id: '0',
            text: 'It doubles.',
          },
          {
            id: '1',
            text: 'It stays the same.',
          },
          {
            id: '2',
            text: 'It halves.',
          },
          {
            id: '3',
            text: 'It becomes zero.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation: 'Both alternative outputs scale equally, leaving the ratio unchanged.',
        evidence: 'Both alternative outputs scale equally, leaving the ratio unchanged.',
        sectionIds: ['cost'],
      },
      {
        id: 'b5-trade-6',
        prompt: 'What does moving along a fixed production frontier represent?',
        options: [
          {
            id: '0',
            text: 'A change in output mix using the same resources and technology',
          },
          {
            id: '1',
            text: 'An automatic improvement in technology',
          },
          {
            id: '2',
            text: 'A doubling of all resources',
          },
          {
            id: '3',
            text: 'Consumption without any scarcity',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation: 'Movement changes allocation, not the frontier itself.',
        evidence: 'Movement changes allocation, not the frontier itself.',
        sectionIds: ['limits'],
      },
      {
        id: 'b5-trade-7',
        prompt: 'Which claim goes beyond what comparative advantage alone establishes?',
        options: [
          {
            id: '0',
            text: 'Relative costs can differ from absolute productivity.',
          },
          {
            id: '1',
            text: 'Trade can expand consumption possibilities.',
          },
          {
            id: '2',
            text: 'Specialization can create potential gains.',
          },
          {
            id: '3',
            text: 'Every affected person must gain from a real-world trade change.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'The model does not guarantee distribution of gains or absence of adjustment costs.',
        evidence:
          'The model does not guarantee distribution of gains or absence of adjustment costs.',
        sectionIds: ['exchange', 'limits'],
      },
      {
        id: 'b5-trade-8',
        prompt: 'A is faster at both tasks. Why might B still efficiently perform one task?',
        options: [
          {
            id: '0',
            text: 'Absolute advantage is unrelated to speed.',
          },
          {
            id: '1',
            text: 'B must have lower money wages.',
          },
          {
            id: '2',
            text: 'B may have the lower opportunity cost in that task.',
          },
          {
            id: '3',
            text: 'A’s productive time has no alternative use.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Relative sacrifice, rather than speed alone, determines comparative advantage.',
        evidence: 'Relative sacrifice, rather than speed alone, determines comparative advantage.',
        sectionIds: ['advantage'],
      },
      {
        id: 'b5-trade-9',
        prompt: 'A point inside the current production frontier can represent which situation?',
        options: [
          {
            id: '0',
            text: 'Some resources are idle or inefficiently used.',
          },
          {
            id: '1',
            text: 'The economy exceeds all technical limits.',
          },
          {
            id: '2',
            text: 'Trade necessarily has stopped.',
          },
          {
            id: '3',
            text: 'Every producer has the same opportunity costs.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation: 'An interior point leaves feasible production opportunities unused.',
        evidence: 'An interior point leaves feasible production opportunities unused.',
        sectionIds: ['limits'],
      },
      {
        id: 'b5-trade-10',
        prompt:
          'Both producers have exactly the same constant opportunity costs for two goods. What follows from this simple model?',
        options: [
          {
            id: '0',
            text: 'Trade must make both worse off.',
          },
          {
            id: '1',
            text: 'Differences in those costs provide no specialization gain.',
          },
          {
            id: '2',
            text: 'One must have comparative advantage in both goods.',
          },
          {
            id: '3',
            text: 'Their absolute productivities must also be identical.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Equal ratios eliminate this model’s source of specialization gain, even if absolute rates differ.',
        evidence:
          'Equal ratios eliminate this model’s source of specialization gain, even if absolute rates differ.',
        sectionIds: ['advantage', 'exchange'],
      },
    ],
  },
  {
    id: 'foundations-clauses',
    category: 'foundations',
    title: 'Sentences, clauses and punctuation',
    topic: 'Sentences, clauses and punctuation',
    difficulty: 'Challenging',
    reading: {
      title: 'Sentences, clauses and punctuation',
      sections: [
        {
          id: 'structure',
          title: 'Find the grammatical core',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A clause contains a subject and a predicate, typically built around a finite verb. The subject identifies what the clause is about; the predicate says something about it. In “The ferry arrived,” ferry is the subject and arrived is the verb. Expanding the sentence with descriptive phrases does not necessarily add another clause.',
            },
            {
              kind: 'paragraph',
              text: 'An independent clause can function as a complete sentence. A dependent clause has a grammatical role within a larger sentence and cannot normally stand alone in formal expository prose. “Although the ferry arrived” has a subject and verb, but although marks it as subordinate and leaves the reader expecting a main assertion. Length is not the test: a very short clause can be independent, and a long group of words can remain incomplete.',
            },
            {
              kind: 'example',
              title: 'Compare three strings',
              blocks: [
                {
                  kind: 'table',
                  caption: 'Look for the core and its status',
                  headers: ['Text', 'Analysis'],
                  rows: [
                    ['The ferry arrived.', 'Independent clause; a complete assertion'],
                    [
                      'Although the ferry arrived',
                      'Dependent clause; concession awaits a main assertion',
                    ],
                    [
                      'The ferry near the old pier',
                      'Noun phrase; no finite verb makes an assertion',
                    ],
                  ],
                },
                {
                  kind: 'paragraph',
                  text: 'Adding many adjectives to the third string would not supply its missing predicate. Adding “was delayed” would: “The ferry near the old pier was delayed.”',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'The standards here concern edited formal prose. Dialogue, notes, headlines and literary writing sometimes use fragments intentionally. Calling something a fragment identifies its structure; whether it is effective depends on the purpose. The quiz states a formal-prose context so that grammatical repair, rather than a stylistic preference for fragments, determines the answer.',
            },
          ],
        },
        {
          id: 'joining',
          title: 'Join complete clauses deliberately',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Two independent clauses can become two sentences. They can also be connected with a semicolon when the relationship is close, or with a comma plus a coordinating conjunction such as and, but or so. A comma alone usually cannot carry that joining function in conventional formal prose; that construction is called a comma splice.',
            },
            {
              kind: 'example',
              title: 'Three sound repairs',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Start with “The ferry arrived, the passengers boarded.” Each side can stand alone. Repair it as “The ferry arrived. The passengers boarded.” Or use “The ferry arrived; the passengers boarded.” Or make the relation explicit: “The ferry arrived, and the passengers boarded.”',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'A run-on is a structural joining problem, not simply a long sentence. “It rained we left” is short but joins complete clauses without a proper boundary. A much longer sentence can be correctly organized. Reading aloud for pauses can help with rhythm, but spoken pauses do not reliably diagnose independent clauses.',
            },
            {
              kind: 'paragraph',
              text: 'Words such as however and therefore express relationships between assertions but do not act like the coordinating conjunction but. Between two independent clauses, a typical formal pattern is “The boat was ready; however, the crew waited.” Replacing the semicolon with a comma creates a splice. A period before However is another valid repair, so a question should not offer both as competing supposedly unique answers.',
            },
          ],
        },
        {
          id: 'dependency',
          title: 'Show which idea depends on which',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Subordinating words such as because, although, when and if make a clause dependent. They also convey different logical relationships: cause, concession, time or condition. Correct punctuation should preserve the intended meaning rather than merely turn an error into any grammatical sentence.',
            },
            {
              kind: 'example',
              title: 'Change the relationship',
              blocks: [
                {
                  kind: 'paragraph',
                  text: '“Because the tide was low, the crew waited” presents low tide as the reason. “Although the tide was low, the crew departed” presents departure despite an obstacle. “When the tide fell, the crew departed” locates the departure in time without necessarily claiming the tide caused it.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'An introductory dependent clause is normally followed by a comma before the main clause. When a closely integrated because-clause follows the main clause, a comma is often unnecessary: “The crew waited because the tide was low.” Commas in other trailing-clause contexts can affect emphasis or meaning, so avoid turning this useful pattern into an absolute rule for every sentence.',
            },
            {
              kind: 'paragraph',
              text: 'A semicolon joins independent clauses in the pattern discussed here. It does not normally attach a dependent clause to its main clause: “Although the tide was low; the crew departed” uses the wrong boundary. The subordinate clause belongs with its main clause, separated by a comma in this introductory position.',
            },
          ],
        },
        {
          id: 'reference',
          title: 'Punctuation can change which people or things are meant',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A restrictive relative clause identifies the subset being discussed. In “The notebooks that have red covers are missing,” the relative clause selects the red-covered notebooks; it does not say every notebook is missing. A supplementary clause adds information about a referent already identified. “These notebooks, which have red covers, are missing” presents the red covers as additional information about all the notebooks already indicated.',
            },
            {
              kind: 'paragraph',
              text: 'The distinction is about meaning, not a mechanical claim that one particular relative pronoun always demands commas. In many varieties of English, which can introduce a restrictive clause. Determine whether the information identifies a subset or supplies an aside. The paired examples deliberately use context and demonstratives to make that distinction clear.',
            },
            {
              kind: 'example',
              title: 'Two different claims',
              blocks: [
                {
                  kind: 'paragraph',
                  text: '“The researchers who checked the data signed the report” identifies the checking researchers. It leaves open whether other researchers did not sign. “The researchers, who checked the data, signed the report” treats the checking as supplementary information about the researchers already in view. Removing commas can therefore change the claim rather than merely its pace.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'For the questions, mark the finite verbs and their subjects first. Then determine whether each clause can stand independently, note any dependency marker, and decide what relationship the sentence is intended to express. Only then choose the punctuation. This sequence is more dependable than counting words or inserting punctuation wherever a speaker might pause.',
            },
            {
              kind: 'paragraph',
              text: 'In revision, preserve both structure and meaning. A period may repair a splice while weakening an explicit causal connection; adding because may supply that connection but also assert a cause the writer did not establish. Good editing checks the claim as well as the grammar. A shorter, technically correct sentence is not automatically a faithful revision of the original idea.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'Purdue OWL · Sentence fragments',
          url: 'https://owl.purdue.edu/owl/general_writing/mechanics/sentence_fragments.html',
        },
        {
          title: 'Purdue OWL · Run-ons and comma splices',
          url: 'https://owl.purdue.edu/owl/general_writing/punctuation/independent_and_dependent_clauses/runonsentences.html',
        },
        {
          title: 'Purdue OWL · Sentence punctuation patterns',
          url: 'https://owl.purdue.edu/owl/general_writing/punctuation/sentence_punctuation_patterns.html',
        },
        {
          title: 'Purdue OWL · Comma rules',
          url: 'https://owl.purdue.edu/owl/general_writing/punctuation/commas/index.html',
        },
      ],
      authorship:
        'Original teaching, examples and questions prepared with AI assistance; references support factual checks.',
    },
    questions: [
      {
        id: 'b5-clauses-1',
        prompt: 'In conventional formal prose, which is a complete sentence?',
        options: [
          {
            id: '0',
            text: 'Although the committee met early.',
          },
          {
            id: '1',
            text: 'The committee near the entrance.',
          },
          {
            id: '2',
            text: 'The committee met early.',
          },
          {
            id: '3',
            text: 'Meeting early near the entrance.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'Only the third has a complete independent subject–predicate assertion.',
        evidence: 'Only the third has a complete independent subject–predicate assertion.',
        sectionIds: ['structure'],
      },
      {
        id: 'b5-clauses-2',
        prompt: 'Which repair correctly joins the independent clauses using a semicolon?',
        options: [
          {
            id: '0',
            text: 'The lights failed; the audience waited.',
          },
          {
            id: '1',
            text: 'The lights; failed the audience waited.',
          },
          {
            id: '2',
            text: 'Although the lights failed; the audience waited.',
          },
          {
            id: '3',
            text: 'The lights failed; and the audience waited.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'The semicolon separates two independent clauses without a coordinating conjunction.',
        evidence:
          'The semicolon separates two independent clauses without a coordinating conjunction.',
        sectionIds: ['structure', 'joining'],
      },
      {
        id: 'b5-clauses-3',
        prompt:
          'Which sentence uses however correctly between independent clauses in formal prose?',
        options: [
          {
            id: '0',
            text: 'The route was open, however the bridge was closed.',
          },
          {
            id: '1',
            text: 'The route was open however, the bridge was closed.',
          },
          {
            id: '2',
            text: 'The route was open; however the bridge, was closed.',
          },
          {
            id: '3',
            text: 'The route was open; however, the bridge was closed.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation: 'A semicolon separates the complete clauses; however is followed by a comma.',
        evidence: 'A semicolon separates the complete clauses; however is followed by a comma.',
        sectionIds: ['joining'],
      },
      {
        id: 'b5-clauses-4',
        prompt: 'What is the problem with “We arrived they left”?',
        options: [
          {
            id: '0',
            text: 'It has too many adjectives.',
          },
          {
            id: '1',
            text: 'Two independent clauses lack a proper joining boundary.',
          },
          {
            id: '2',
            text: 'It contains no finite verb.',
          },
          {
            id: '3',
            text: 'Both clauses are dependent.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation: 'Each side can stand alone, but no punctuation or conjunction joins them.',
        evidence: 'Each side can stand alone, but no punctuation or conjunction joins them.',
        sectionIds: ['structure', 'joining'],
      },
      {
        id: 'b5-clauses-5',
        prompt:
          'Which word makes the relationship explicitly causal: “___ the pump failed, the basement flooded”?',
        options: [
          {
            id: '0',
            text: 'Although',
          },
          {
            id: '1',
            text: 'Because',
          },
          {
            id: '2',
            text: 'When',
          },
          {
            id: '3',
            text: 'Unless',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation: 'Because asserts a reason; when primarily indicates timing.',
        evidence: 'Because asserts a reason; when primarily indicates timing.',
        sectionIds: ['dependency'],
      },
      {
        id: 'b5-clauses-6',
        prompt: 'Which sentence correctly punctuates the introductory dependent clause?',
        options: [
          {
            id: '0',
            text: 'If the gate opens; we will enter.',
          },
          {
            id: '1',
            text: 'If, the gate opens we will enter.',
          },
          {
            id: '2',
            text: 'If the gate opens we, will enter.',
          },
          {
            id: '3',
            text: 'If the gate opens, we will enter.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation: 'The introductory if-clause takes a comma before the main clause.',
        evidence: 'The introductory if-clause takes a comma before the main clause.',
        sectionIds: ['dependency'],
      },
      {
        id: 'b5-clauses-7',
        prompt: '“The files that contain images are large.” What does the restrictive clause do?',
        options: [
          {
            id: '0',
            text: 'Selects which files are being described as large',
          },
          {
            id: '1',
            text: 'States that all files contain images',
          },
          {
            id: '2',
            text: 'Makes the whole sentence a fragment',
          },
          {
            id: '3',
            text: 'Joins two independent clauses',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation: 'The clause narrows the referent to image-containing files.',
        evidence: 'The clause narrows the referent to image-containing files.',
        sectionIds: ['reference'],
      },
      {
        id: 'b5-clauses-8',
        prompt: 'Why is sentence length an unreliable test for a run-on?',
        options: [
          {
            id: '0',
            text: 'All long sentences are run-ons.',
          },
          {
            id: '1',
            text: 'All short sentences are fragments.',
          },
          {
            id: '2',
            text: 'Run-ons concern clause joining, not word count.',
          },
          {
            id: '3',
            text: 'Punctuation has no relationship to clauses.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'Even a very short sentence can improperly fuse independent clauses.',
        evidence: 'Even a very short sentence can improperly fuse independent clauses.',
        sectionIds: ['joining'],
      },
      {
        id: 'b5-clauses-9',
        prompt:
          'Which revision preserves the contrast in “Although the trial was brief, it was informative”?',
        options: [
          {
            id: '0',
            text: 'The trial was informative because it was brief.',
          },
          {
            id: '1',
            text: 'The trial was brief, but it was informative.',
          },
          {
            id: '2',
            text: 'The trial was brief, so it was informative.',
          },
          {
            id: '3',
            text: 'The trial was informative only if it was brief.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'But preserves concession/contrast; the other versions introduce cause or condition.',
        evidence:
          'But preserves concession/contrast; the other versions introduce cause or condition.',
        sectionIds: ['joining', 'dependency'],
      },
      {
        id: 'b5-clauses-10',
        prompt:
          'In “These samples, which were frozen, survived,” what is the role of the comma-enclosed clause?',
        options: [
          {
            id: '0',
            text: 'It adds information about the already identified samples.',
          },
          {
            id: '1',
            text: 'It selects only the frozen subset from these samples.',
          },
          {
            id: '2',
            text: 'It supplies the sentence’s only main verb.',
          },
          {
            id: '3',
            text: 'It makes the samples a new independent sentence.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation: 'The supplementary clause adds information about these identified samples.',
        evidence: 'The supplementary clause adds information about these identified samples.',
        sectionIds: ['structure', 'reference'],
      },
    ],
  },
  {
    id: 'foundations-watersheds',
    category: 'foundations',
    title: 'Rivers and drainage basins',
    topic: 'Rivers and drainage basins',
    difficulty: 'Challenging',
    reading: {
      title: 'Rivers and drainage basins',
      sections: [
        {
          id: 'basin',
          title: 'Follow water to a shared outlet',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A watershed, or drainage basin, is the land area draining toward a particular outlet. The outlet may be a stream junction, reservoir or river mouth. A basin boundary usually follows topographic divides: ridges or high ground separating downhill surface-flow paths. The boundary is defined by drainage, not by the outline of a town or the nearest named river on a map.',
            },
            {
              kind: 'paragraph',
              text: 'Watersheds are nested. A small tributary basin can lie inside a larger river basin. Changing the outlet changes the area being considered: moving the outlet downstream beyond a tributary junction adds the tributary’s contributing land. A location can therefore belong to a small local basin and a much larger regional basin without contradiction.',
            },
            {
              kind: 'example',
              title: 'Two streams meet',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Stream A drains 12 km² and stream B drains 8 km² of separate land. They join before a gauge, and another 3 km² drains directly to the short reach above the gauge. The gauge’s drainage area is 23 km². Adding the areas is appropriate because the three contributing regions do not overlap. Counting a nested sub-basin again would double-count land.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Actual water pathways include infiltration and groundwater as well as overland flow. A topographic surface divide is a useful starting model, not a guarantee that every subsurface flow path obeys the same boundary. Engineered channels and water transfers can also cross natural divides. The questions specify ordinary surface drainage when that is the intended model.',
            },
          ],
        },
        {
          id: 'runoff',
          title: 'Rainfall is partitioned among several pathways',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Not every drop of rain immediately reaches a river. Water can be intercepted, stored temporarily, infiltrate soil, evaporate or run over the surface. Infiltration is entry into the ground; runoff is water moving toward channels over or near the surface. The fraction becoming rapid runoff depends on soil, vegetation, prior wetness, slope and the intensity and duration of rainfall.',
            },
            {
              kind: 'paragraph',
              text: 'Paving reduces permeable surface area and can connect runoff efficiently to drains. For the same storm, a developed catchment can therefore send water to its outlet more quickly and produce a higher peak flow than a more permeable catchment. The exact response is site-dependent. “All rain becomes runoff” would ignore storage, losses and variations in infrastructure.',
            },
            {
              kind: 'example',
              title: 'A stated runoff fraction',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Suppose 20 mm of rain falls uniformly over 2 km² and exactly one quarter becomes direct runoff. Convert the depth to 0.02 m and the area to 2,000,000 m². Rain volume is 40,000 m³; direct runoff volume is 10,000 m³.',
                },
                {
                  kind: 'equation',
                  label: 'Depth over an area',
                  lines: [
                    {
                      left: 'volume',
                      right: 'rain depth × contributing area',
                      reason:
                        'A depth in meters times an area in square meters gives cubic meters.',
                    },
                  ],
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'A hydrograph plots discharge against time. A taller, narrower storm response indicates a more concentrated flow pulse; it does not alone tell you the total volume. Volume corresponds to the accumulated flow over time, the area under the discharge curve. Two pulses can have the same total volume but different peaks and therefore different immediate flooding consequences.',
            },
          ],
        },
        {
          id: 'flow',
          title: 'Discharge is volume per time',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Discharge measures how much water passes a cross-section per unit time, commonly cubic meters per second. In a simplified cross-section, multiply water-filled area by average downstream velocity. A square meter of area moving one meter downstream in a second sweeps out one cubic meter, explaining the units and the relationship.',
            },
            {
              kind: 'equation',
              label: 'Flow through a cross-section',
              lines: [
                {
                  left: 'Q',
                  right: 'A × v',
                  reason:
                    'A is cross-sectional area and v is mean downstream speed over that area.',
                },
              ],
            },
            {
              kind: 'example',
              title: 'Compare two sections',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'A channel section 4 m wide and 1 m deep has area 4 m² in a rectangular approximation. At mean speed 0.75 m/s, its discharge is 3 m³/s. A downstream section with area 6 m² carries the same steady discharge at mean speed 0.5 m/s if no water enters, leaves or accumulates between them.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Real channels have irregular beds and uneven velocity, so a surface speed at one point is not necessarily the cross-sectional mean. The simplified formula still explains how measurements must be combined. At a confluence, steady discharges add if there are no intervening losses or storage changes; widths alone do not add in the same way.',
            },
          ],
        },
        {
          id: 'sediment',
          title: 'A river changes the ground it crosses',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Weathering breaks down or alters material in place. Erosion removes and transports it; deposition occurs when transported material settles or accumulates. A river can erode one bank while depositing on another, so these are linked parts of landscape change rather than mutually exclusive descriptions of an entire river.',
            },
            {
              kind: 'paragraph',
              text: 'In a typical meander, erosion acts strongly on the outer bend and deposition builds a point bar on the inner bend. Over time the bend can migrate. If a shortcut cuts across a narrow neck, the abandoned loop may become an oxbow lake. The pattern describes an evolving channel, not a fixed curve that simply grows uniformly outward.',
            },
            {
              kind: 'table',
              caption: 'Read the landform',
              headers: ['Feature or process', 'Interpretation'],
              rows: [
                ['Outer bend retreat', 'Bank erosion'],
                ['Inner bend bar', 'Sediment deposition'],
                ['Abandoned loop after cutoff', 'Former channel separated from the main route'],
              ],
            },
            {
              kind: 'paragraph',
              text: 'A floodplain is low-lying land beside a river shaped in part by channel movement and overbank deposition. A place can be dry during ordinary flow yet still be part of a floodplain. Building a barrier or straightening a reach changes the system’s behavior but does not remove the incoming water or sediment from the larger basin.',
            },
            {
              kind: 'paragraph',
              text: 'Apply the chapter by tracing a sequence: where does precipitation fall, which paths carry it, how quickly does it reach the outlet, and what happens to transported material? This connects the basin-area calculation with the runoff-volume and discharge questions. It also explains why management upstream can affect places downstream even when administrative boundaries separate them.',
            },
            {
              kind: 'paragraph',
              text: 'For the numerical questions, keep depth, volume and rate distinct. A rainfall depth of 10 mm is not a discharge, and a discharge of 10 m³/s is not a fixed total volume until a duration is specified. Multiplying a constant discharge by seconds gives volume. Confusing these dimensions often produces plausible-looking numbers with the wrong physical meaning.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'USGS · Watersheds and drainage basins',
          url: 'https://www.usgs.gov/water-science-school/science/watersheds-and-drainage-basins',
        },
        {
          title: 'USGS · Surface runoff and the water cycle',
          url: 'https://www.usgs.gov/water-science-school/science/surface-runoff-and-water-cycle',
        },
        {
          title: 'USGS · Meanders',
          url: 'https://www.usgs.gov/educational-resources/find-feature-meander',
        },
      ],
      authorship:
        'Original teaching, examples and questions prepared with AI assistance; references support factual checks.',
    },
    questions: [
      {
        id: 'b5-watersheds-1',
        prompt: 'What defines a surface-water drainage basin for a chosen outlet?',
        options: [
          {
            id: '0',
            text: 'All land within the same county',
          },
          {
            id: '1',
            text: 'All land closer to that outlet than any other',
          },
          {
            id: '2',
            text: 'Land whose surface drainage leads to that outlet',
          },
          {
            id: '3',
            text: 'Only land occupied by the river channel',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'A basin is defined by contributing drainage paths, not proximity or administrative borders.',
        evidence:
          'A basin is defined by contributing drainage paths, not proximity or administrative borders.',
        sectionIds: ['basin'],
      },
      {
        id: 'b5-watersheds-2',
        prompt:
          'A smaller tributary basin lies inside a larger river basin. Which statement is correct?',
        options: [
          {
            id: '0',
            text: 'A place can belong to both, depending on the chosen outlet.',
          },
          {
            id: '1',
            text: 'The two basin definitions contradict each other.',
          },
          {
            id: '2',
            text: 'The smaller basin must drain away from the river.',
          },
          {
            id: '3',
            text: 'The larger basin excludes all tributaries.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation: 'Watersheds are nested according to outlet choice.',
        evidence: 'Watersheds are nested according to outlet choice.',
        sectionIds: ['basin'],
      },
      {
        id: 'b5-watersheds-3',
        prompt: 'Ten millimeters of rain falls over 1 km². Before any losses, what volume is that?',
        options: [
          {
            id: '0',
            text: '10 m³',
          },
          {
            id: '1',
            text: '100 m³',
          },
          {
            id: '2',
            text: '1,000 m³',
          },
          {
            id: '3',
            text: '10,000 m³',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation: 'Convert depth and area to meters first: 0.01 m × 1,000,000 m² = 10,000 m³.',
        evidence: 'Convert depth and area to meters first: 0.01 m × 1,000,000 m² = 10,000 m³.',
        sectionIds: ['runoff'],
      },
      {
        id: 'b5-watersheds-4',
        prompt:
          'A rectangular water cross-section is 5 m wide and 2 m deep, with mean speed 0.4 m/s. Discharge?',
        options: [
          {
            id: '0',
            text: '2 m³/s',
          },
          {
            id: '1',
            text: '4 m³/s',
          },
          {
            id: '2',
            text: '10 m³/s',
          },
          {
            id: '3',
            text: '25 m³/s',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'The cross-section has area 10 m²; multiplying by mean speed gives Q=10×0.4=4 m³/s.',
        evidence:
          'The cross-section has area 10 m²; multiplying by mean speed gives Q=10×0.4=4 m³/s.',
        sectionIds: ['flow'],
      },
      {
        id: 'b5-watersheds-5',
        prompt:
          'For a typical meander, where are erosion and deposition commonly strongest respectively?',
        options: [
          {
            id: '0',
            text: 'Inner bend; outer bend',
          },
          {
            id: '1',
            text: 'Outer bend; inner bend',
          },
          {
            id: '2',
            text: 'Both only at the river source',
          },
          {
            id: '3',
            text: 'Both uniformly across every bank',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation: 'Outer-bank erosion and inner-bank deposition shift the bend.',
        evidence: 'Outer-bank erosion and inner-bank deposition shift the bend.',
        sectionIds: ['sediment'],
      },
      {
        id: 'b5-watersheds-6',
        prompt: 'Why can paving increase a storm’s peak discharge?',
        options: [
          {
            id: '0',
            text: 'It increases infiltration everywhere.',
          },
          {
            id: '1',
            text: 'It changes all rainfall into groundwater.',
          },
          {
            id: '2',
            text: 'It eliminates the watershed outlet.',
          },
          {
            id: '3',
            text: 'It can reduce infiltration and route water to channels more quickly.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation: 'Impermeable connected surfaces can concentrate runoff more rapidly.',
        evidence: 'Impermeable connected surfaces can concentrate runoff more rapidly.',
        sectionIds: ['runoff'],
      },
      {
        id: 'b5-watersheds-7',
        prompt:
          'Two steady tributaries carry 3 and 5 m³/s into a reach with no losses or accumulation. Downstream discharge?',
        options: [
          {
            id: '0',
            text: '8 m³/s',
          },
          {
            id: '1',
            text: '2 m³/s',
          },
          {
            id: '2',
            text: '15 m³/s',
          },
          {
            id: '3',
            text: '4 m³/s',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation: 'Conservation gives the sum of incoming volume rates.',
        evidence: 'Conservation gives the sum of incoming volume rates.',
        sectionIds: ['flow'],
      },
      {
        id: 'b5-watersheds-8',
        prompt: 'An oxbow lake commonly develops through which process?',
        options: [
          {
            id: '0',
            text: 'A river’s source freezes permanently.',
          },
          {
            id: '1',
            text: 'Sediment dissolves the entire watershed divide.',
          },
          {
            id: '2',
            text: 'A meander cutoff separates an old loop from the main channel.',
          },
          {
            id: '3',
            text: 'Two unrelated rivers necessarily exchange sources.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'A cutoff can isolate the former bend.',
        evidence: 'A cutoff can isolate the former bend.',
        sectionIds: ['sediment'],
      },
      {
        id: 'b5-watersheds-9',
        prompt: 'Two hydrographs have equal total area but different peaks. What does that imply?',
        options: [
          {
            id: '0',
            text: 'Equal maximum discharge',
          },
          {
            id: '1',
            text: 'Equal total flow volume over the plotted intervals',
          },
          {
            id: '2',
            text: 'Equal duration of flow',
          },
          {
            id: '3',
            text: 'Identical flood risk at every location',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation: 'Area under discharge versus time represents volume, not peak rate.',
        evidence: 'Area under discharge versus time represents volume, not peak rate.',
        sectionIds: ['runoff', 'flow'],
      },
      {
        id: 'b5-watersheds-10',
        prompt:
          'A rock breaks down in place; fragments are then carried away by water. Which sequence describes those processes?',
        options: [
          {
            id: '0',
            text: 'Weathering, then erosion',
          },
          {
            id: '1',
            text: 'Deposition, then weathering',
          },
          {
            id: '2',
            text: 'Erosion, then condensation',
          },
          {
            id: '3',
            text: 'Infiltration, then deposition',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation: 'Weathering alters material in place; erosion removes and transports it.',
        evidence: 'Weathering alters material in place; erosion removes and transports it.',
        sectionIds: ['sediment'],
      },
    ],
  },
];
